import { Box, Typography, Card, alpha } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import SecurityIcon from '@mui/icons-material/Security';
import PersonIcon from '@mui/icons-material/Person';
import DevicesIcon from '@mui/icons-material/Devices';
import Grid from '@mui/material/Grid';
import { colors } from '../../theme';

interface FeatureItemProps {
    icon: React.ReactNode;
    title: string;
    description: string;
}

const FeatureItem = ({ icon, title, description }: FeatureItemProps) => (
    <Box display="flex" alignItems="flex-start" gap={2}>
        <Box
            sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                background: alpha(colors.primary, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colors.primary,
                flexShrink: 0,
            }}
        >
            {icon}
        </Box>
        <Box>
            <Typography
                sx={{
                    fontWeight: 600,
                    color: colors.slate[900],
                    fontSize: '0.875rem',
                }}
            >
                {title}
            </Typography>
            <Typography
                variant="body2"
                sx={{ color: colors.slate[500], mt: 0.25 }}
            >
                {description}
            </Typography>
        </Box>
    </Box>
);

export const AuditLogList = () => (
    <Box>
        {/* Page Header */}
        <Box mb={4}>
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 700,
                    color: colors.slate[900],
                    letterSpacing: '-0.5px',
                }}
            >
                Audit Logs
            </Typography>
            <Typography
                variant="body1"
                sx={{ color: colors.slate[500], mt: 0.5 }}
            >
                Track system activity and security events
            </Typography>
        </Box>

        {/* Coming Soon Card */}
        <Card sx={{ overflow: 'hidden' }}>
            {/* Gradient Header */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.tertiary})`,
                    p: 4,
                    textAlign: 'center',
                }}
            >
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        background: 'rgba(255, 255, 255, 0.15)',
                        borderRadius: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px',
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <HistoryIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: 'white', mb: 1 }}
                >
                    Coming Soon
                </Typography>
                <Typography
                    sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        maxWidth: 400,
                        mx: 'auto',
                    }}
                >
                    Comprehensive audit logging to track all system activity,
                    user actions, and security events.
                </Typography>
            </Box>

            {/* Features Preview */}
            <Box sx={{ p: 4 }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        color: colors.slate[900],
                        mb: 3,
                    }}
                >
                    Planned Features
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <FeatureItem
                            icon={<SecurityIcon sx={{ fontSize: 20 }} />}
                            title="Security Events"
                            description="Track login attempts, permission changes, and access control events"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FeatureItem
                            icon={<PersonIcon sx={{ fontSize: 20 }} />}
                            title="User Activity"
                            description="Monitor user actions including session management and impersonation"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FeatureItem
                            icon={<DevicesIcon sx={{ fontSize: 20 }} />}
                            title="Device Events"
                            description="Log device provisioning, configuration changes, and status updates"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FeatureItem
                            icon={<HistoryIcon sx={{ fontSize: 20 }} />}
                            title="Full History"
                            description="Searchable audit trail with filtering by date, user, and event type"
                        />
                    </Grid>
                </Grid>
            </Box>
        </Card>
    </Box>
);